import { cn } from '@lashpic/ui/admin';
import { Command } from 'cmdk';
import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { NAV_GROUPS } from '../lib/nav-config';

/**
 * ⌘K 팔레트 — dashfolio DNA 적용.
 *
 * 특징:
 *   - 사이드바 상단 검색 트리거 (dashfolio "Search…" 카드 스타일)
 *   - 우측 단축키 ⌘K 뱃지 (admin-kbd-hint 유틸)
 *   - 모달은 불투명 surface + hairline (Vibrancy 미사용)
 *
 * TODO(#5 Jupiter): LLM 의도 파싱 + 회원/시술/실험 id fuzzy 검색 통합.
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      {/* 트리거 — dashfolio 검색 카드 */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'admin-focus-ring flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-[13px] transition-colors',
          'border border-[var(--color-admin-hairline)] bg-[var(--color-admin-surface)] text-[var(--color-admin-text-secondary)]',
          'hover:border-[var(--color-admin-hairline-strong)] hover:bg-[var(--color-admin-surface-2)]',
        )}
        aria-label="명령 팔레트 열기 (⌘K)"
      >
        <Search className="size-3.5 shrink-0" aria-hidden />
        <span className="flex-1 truncate">검색…</span>
        <kbd className="admin-kbd-hint">⌘K</kbd>
      </button>

      {/* 다이얼로그 */}
      {open ? (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 px-4 pt-24 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          role="presentation"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="명령 팔레트"
            className="w-full max-w-lg overflow-hidden rounded-xl border border-[var(--color-admin-hairline-strong)] bg-[var(--color-admin-surface)] shadow-[var(--shadow-admin-modal)]"
          >
            <Command label="Lashpic 관리자 명령 팔레트">
              <div className="hairline-b flex items-center gap-2 px-4 py-3">
                <Search
                  className="size-4 text-[var(--color-admin-text-secondary)]"
                  aria-hidden
                />
                <Command.Input
                  placeholder="페이지, 회원, 시술 또는 자연어로 검색…"
                  className="admin-focus-ring flex-1 bg-transparent text-[14px] outline-none placeholder:text-[var(--color-admin-text-tertiary)]"
                  autoFocus
                />
                <kbd className="admin-kbd-hint">ESC</kbd>
              </div>
              <Command.List className="max-h-[60vh] overflow-y-auto p-2">
                <Command.Empty className="px-3 py-6 text-center text-[13px] text-[var(--color-admin-text-secondary)]">
                  결과가 없습니다.
                </Command.Empty>

                {NAV_GROUPS.map((group) => (
                  <Command.Group
                    key={group.section}
                    heading={group.heading}
                    className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.12em] [&_[cmdk-group-heading]]:text-[var(--color-admin-text-tertiary)]"
                  >
                    {group.items.map((item) => {
                      const Icon = item.icon;
                      return (
                        <Command.Item
                          key={item.path}
                          value={`${group.heading} ${item.label} ${item.path}`}
                          onSelect={() => {
                            navigate(item.path);
                            setOpen(false);
                          }}
                          className="flex cursor-pointer items-center gap-2.5 rounded-md px-3 py-2 text-[13px] text-[var(--color-admin-text)] aria-selected:bg-[var(--color-admin-accent-soft)] aria-selected:text-[var(--color-admin-accent)]"
                        >
                          <Icon className="size-4" aria-hidden />
                          <span>{item.label}</span>
                          <span className="ml-auto font-mono text-[11px] text-[var(--color-admin-text-tertiary)]">
                            {item.path}
                          </span>
                        </Command.Item>
                      );
                    })}
                  </Command.Group>
                ))}
              </Command.List>
            </Command>
          </div>
        </div>
      ) : null}
    </>
  );
}
